import { useCallback, useEffect, useState } from "react";
import { useWatch } from "react-hook-form";
import { observer } from "mobx-react-lite";
import { Plus, RefreshCw, X } from "lucide-react";
import { MonitorableParameterType, parameters as parametersApi } from "@/endpoints/dryp/parameters";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { FormField, FormItem, FormControl, FormMessage } from "@/components/ui/form";
import { useAsyncStateStore } from "@/hooks/useAsyncStateStore";
import { cn } from "@/lib/utils";
import { OPERATOR_LABELS } from "../types";
import { FieldGroup } from "./components/FieldGroup";
import { useAlarmWizardStore } from "./useAlarmWizardStore";

type TriggerMode = "single" | "sustained" | "datapoints";

const TRIGGER_MODES: { value: TriggerMode; label: string; hint: string }[] = [
	{ value: "single", label: "Single reading", hint: "Fires on one measurement" },
	{ value: "sustained", label: "Sustained", hint: "Must persist for a duration" },
	{ value: "datapoints", label: "Data points", hint: "A number of readings in a row" },
];

const warningRelationshipHint = (
	operator: Dryp.ComparisonOperator | undefined,
	criticalThreshold: number | null | undefined,
	unitSymbol: string,
): string => {
	if (!operator || operator === "=") {
		return "Choose a >, ≥, <, or ≤ operator to add an earlier warning level.";
	}
	const ascending = operator === ">" || operator === ">=";
	const side = ascending ? "at or below" : "at or above";
	const critical =
		criticalThreshold != null && !Number.isNaN(criticalThreshold)
			? ` the critical value (${criticalThreshold}${unitSymbol ? ` ${unitSymbol}` : ""})`
			: " the critical value";
	return `Warning triggers earlier than critical — set it ${side}${critical}.`;
};

const TriggerModeNumberInput = ({
	value,
	placeholder,
	suffix,
	onValue,
}: {
	value: number | null | undefined;
	placeholder: string;
	suffix: string;
	onValue: (value: number) => void;
}) => (
	<div className="flex items-center gap-3">
		<Input
			type="number"
			min={1}
			placeholder={placeholder}
			value={value ?? ""}
			onChange={e => onValue(e.target.value ? Number(e.target.value) : 1)}
			className="w-36"
		/>
		<span className="text-sm text-muted-foreground">{suffix}</span>
	</div>
);

const TriggerModeToggle = ({
	durationMinutes,
	datapointCount,
	onChange,
}: {
	durationMinutes: number | null | undefined;
	datapointCount: number | null | undefined;
	onChange: (next: { durationMinutes: number | null; datapointCount: number | null }) => void;
}) => {
	const mode: TriggerMode =
		(durationMinutes ?? 0) > 0 ? "sustained" : (datapointCount ?? 0) > 0 ? "datapoints" : "single";

	const selectMode = (next: TriggerMode) => {
		if (next === mode) return;
		if (next === "single") onChange({ durationMinutes: null, datapointCount: null });
		else if (next === "sustained") onChange({ durationMinutes: 30, datapointCount: null });
		else onChange({ durationMinutes: null, datapointCount: 3 });
	};

	return (
		<div className="space-y-2.5">
			<div className="grid grid-cols-3 gap-2">
				{TRIGGER_MODES.map(option => {
					const isSelected = option.value === mode;
					return (
						<button
							key={option.value}
							type="button"
							onClick={() => selectMode(option.value)}
							className={cn(
								"flex flex-col gap-0.5 rounded-lg border-2 px-3 py-2.5 text-left transition-all",
								isSelected ? "border-primary bg-primary/5" : "border-border hover:bg-accent/50",
							)}
						>
							<span className={cn("text-xs font-medium", isSelected && "text-primary")}>
								{option.label}
							</span>
							<span className="text-[10px] text-muted-foreground leading-snug">{option.hint}</span>
						</button>
					);
				})}
			</div>
			{mode === "sustained" && (
				<TriggerModeNumberInput
					value={durationMinutes}
					placeholder="e.g. 30"
					suffix="minutes"
					onValue={minutes => onChange({ durationMinutes: minutes, datapointCount: null })}
				/>
			)}
			{mode === "datapoints" && (
				<TriggerModeNumberInput
					value={datapointCount}
					placeholder="e.g. 3"
					suffix="readings in a row (within 7 days)"
					onValue={count => onChange({ durationMinutes: null, datapointCount: count })}
				/>
			)}
		</div>
	);
};

export const StepCondition = observer(() => {
	const state = useAlarmWizardStore();
	const alarmType = useWatch({ control: state.form.control, name: "alarmType" });
	const parameterId = useWatch({ control: state.form.control, name: "parameterId" });
	const warningThreshold = useWatch({ control: state.form.control, name: "warningThreshold" });
	const criticalThreshold = useWatch({ control: state.form.control, name: "criticalThreshold" });
	const durationMinutes = useWatch({ control: state.form.control, name: "durationMinutes" });
	const datapointCount = useWatch({ control: state.form.control, name: "datapointCount" });
	const operator = useWatch({ control: state.form.control, name: "operator" });

	const [showWarning, setShowWarning] = useState(() => warningThreshold != null);

	const paramsAlarmCategory: MonitorableParameterType = alarmType === "device_health" ? "Device" : "Hydraulic";
	const parameterApiObj = useAsyncStateStore(parametersApi.getMonitorableParameters);

	const fetchParameters = useCallback(() => {
		// Swallow the rejection: any error is surfaced via `parameterApiObj.error` below.
		parameterApiObj.fetch(paramsAlarmCategory).catch(() => {});
	}, [paramsAlarmCategory, parameterApiObj]);

	useEffect(() => {
		fetchParameters();
	}, [fetchParameters]);

	const parameters = parameterApiObj.data || [];
	const loadingParameters = parameterApiObj.loading;
	const parametersError = parameterApiObj.error as ApiError | null;
	const selectedParameter = parameters.find(param => param.id === parameterId);
	const unitSymbol = selectedParameter?.unitSymbol ?? "";

	// In edit mode the form's defaults were captured by `form.reset({...details})`,
	// so resetting/clearing in StepAlarmType doesn't take. Guard here: once the
	// new alarm-type's parameters arrive, drop any parameterId that's not in the
	useEffect(() => {
		if (parameterApiObj.loading || !parameterApiObj.loaded || parameters.length === 0) return;
		if (parameterId != null && !parameters.some(param => param.id === parameterId)) {
			state.form.setValue("parameterId", undefined as never, { shouldDirty: true });
			state.form.setValue("criticalThreshold", undefined as never, { shouldDirty: true });
			state.form.setValue("warningThreshold", null, { shouldDirty: true });
			state.form.setValue("operator", undefined as never, { shouldDirty: true });
			setShowWarning(false);
		}
	}, [parameters, parameterApiObj.loading, parameterApiObj.loaded, parameterId, state.form]);

	const removeWarningTier = () => {
		setShowWarning(false);
		state.form.setValue("warningThreshold", null, { shouldValidate: true });
		state.form.setValue("warningDeliveryMode", null, { shouldDirty: true });
	};

	return (
		<div className="space-y-8">
			<div>
				<h3 className="text-base font-semibold">Condition</h3>
				<p className="text-sm text-muted-foreground mt-0.5">
					Set the metric and the thresholds that will trigger this alarm.
				</p>
			</div>

			<div className="space-y-5">
				<FormField
					key={`parameterId-${alarmType}`}
					control={state.form.control}
					name="parameterId"
					render={({ field }) => (
						<FormItem>
							<FieldGroup label="What" hint="The attribute to monitor.">
								{loadingParameters ? (
									<Skeleton className="h-9 w-full" />
								) : parametersError ? (
									<div className="flex items-center justify-between gap-3 rounded-md bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive">
										<span>{parametersError.message || "Failed to load parameters."}</span>
										<button
											type="button"
											onClick={fetchParameters}
											className="inline-flex items-center gap-1.5 text-xs font-medium underline-offset-2 hover:underline shrink-0"
										>
											<RefreshCw className="size-3.5" />
											Retry
										</button>
									</div>
								) : (
									<FormControl>
										<Select
											value={field.value != null ? String(field.value) : undefined}
											onValueChange={value => {
												const param = parameters.find(p => p.id === Number(value));
												field.onChange(param?.id);
											}}
										>
											<SelectTrigger>
												<SelectValue placeholder="Select a metric" />
											</SelectTrigger>
											<SelectContent>
												{parameters.map(param => (
													<SelectItem key={param.id} value={String(param.id)}>
														{param.name}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									</FormControl>
								)}
							</FieldGroup>
							<FormMessage />
						</FormItem>
					)}
				/>

				<FieldGroup
					label="When"
					hint="The threshold that triggers a critical alert. Add a warning level to be notified earlier."
				>
					{/* Critical tier */}
					<div className="rounded-lg border border-destructive/25 bg-destructive/[0.04] p-3 space-y-2 dark:border-destructive/30 dark:bg-destructive/[0.07]">
						<span className="text-[11px] font-semibold uppercase tracking-wide text-destructive/80">
							Critical
						</span>
						<div className="flex items-center gap-2">
							<FormField
								control={state.form.control}
								name="operator"
								render={({ field }) => (
									<FormItem className="space-y-0">
										<FormControl>
											<Select value={field.value} onValueChange={field.onChange}>
												<SelectTrigger className="w-52 bg-background">
													<SelectValue placeholder="Operator" />
												</SelectTrigger>
												<SelectContent>
													{(
														Object.entries(OPERATOR_LABELS) as [
															Dryp.ComparisonOperator,
															string,
														][]
													).map(([value, label]) => (
														<SelectItem key={value} value={value}>
															{label}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
										</FormControl>
									</FormItem>
								)}
							/>
							<FormField
								control={state.form.control}
								name="criticalThreshold"
								render={({ field }) => (
									<FormItem className="space-y-0">
										<FormControl>
											<Input
												type="number"
												placeholder="Value"
												value={field.value ?? ""}
												onChange={e =>
													field.onChange(e.target.value ? Number(e.target.value) : undefined)
												}
												className="w-32 bg-background"
											/>
										</FormControl>
									</FormItem>
								)}
							/>
							{unitSymbol && <span className="text-sm text-muted-foreground shrink-0">{unitSymbol}</span>}
						</div>
						<div className="flex gap-2">
							<FormField control={state.form.control} name="operator" render={() => <FormMessage />} />
							<FormField
								control={state.form.control}
								name="criticalThreshold"
								render={() => <FormMessage />}
							/>
						</div>
					</div>

					{/* Warning tier (optional) */}
					{showWarning ? (
						<div className="mt-3 space-y-2 rounded-lg border border-amber-300/50 bg-amber-50/40 p-3 dark:border-amber-500/25 dark:bg-amber-500/[0.05]">
							<div className="flex items-center justify-between">
								<span className="text-[11px] font-semibold uppercase tracking-wide text-amber-700/90 dark:text-amber-400/90">
									Warning
								</span>
								<button
									type="button"
									onClick={removeWarningTier}
									className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs text-muted-foreground hover:text-destructive transition-colors"
									aria-label="Remove warning level"
								>
									<X className="size-3.5" />
									Remove
								</button>
							</div>
							<div className="flex items-center gap-2">
								{/* Operator is shared with the critical tier; shown read-only for context. */}
								<div className="flex h-9 w-52 items-center rounded-md border border-input bg-background px-3 text-sm text-muted-foreground">
									{operator ? OPERATOR_LABELS[operator] : "Same operator"}
								</div>
								<FormField
									control={state.form.control}
									name="warningThreshold"
									render={({ field }) => (
										<FormItem className="space-y-0">
											<FormControl>
												<Input
													type="number"
													placeholder="Value"
													value={field.value ?? ""}
													onChange={e =>
														field.onChange(e.target.value ? Number(e.target.value) : null)
													}
													className="w-32 bg-background"
												/>
											</FormControl>
										</FormItem>
									)}
								/>
								{unitSymbol && (
									<span className="text-sm text-muted-foreground shrink-0">{unitSymbol}</span>
								)}
							</div>
							<p className="text-xs text-muted-foreground">
								{warningRelationshipHint(operator, criticalThreshold, unitSymbol)}
							</p>
							<FormField
								control={state.form.control}
								name="warningThreshold"
								render={() => <FormMessage />}
							/>
						</div>
					) : (
						<button
							type="button"
							onClick={() => setShowWarning(true)}
							className="mt-3 flex w-full items-center gap-2.5 rounded-lg border border-dashed border-amber-300/70 bg-transparent px-3 py-2.5 text-left transition-colors hover:border-amber-400 hover:bg-amber-50/50 dark:border-amber-500/30 dark:hover:bg-amber-500/[0.06]"
						>
							<Plus className="size-4 text-amber-600 dark:text-amber-400 shrink-0" />
							<span className="flex flex-col">
								<span className="text-sm font-medium text-amber-700 dark:text-amber-300">
									Add a warning level
								</span>
								<span className="text-xs text-muted-foreground">
									Get an earlier, less-urgent alert before the condition turns critical.
								</span>
							</span>
						</button>
					)}
				</FieldGroup>

				<FieldGroup label="How" hint="How the condition must hold before the alarm fires.">
					<TriggerModeToggle
						durationMinutes={durationMinutes}
						datapointCount={datapointCount}
						onChange={next => {
							state.form.setValue("durationMinutes", next.durationMinutes, { shouldValidate: true });
							state.form.setValue("datapointCount", next.datapointCount, { shouldValidate: true });
						}}
					/>
					<div className="mt-1 flex gap-2">
						<FormField control={state.form.control} name="durationMinutes" render={() => <FormMessage />} />
						<FormField control={state.form.control} name="datapointCount" render={() => <FormMessage />} />
					</div>
				</FieldGroup>
			</div>
		</div>
	);
});
